import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Vote } from 'lucide-react';
import {
  getPublicDonatePoll,
  voteDonatePoll,
  DonatePoll,
} from '../../../api/payment/donate-poll.api';

const formatCa = (v: number) => Number(v || 0).toLocaleString('vi-VN');

const countdown = (s: number) => {
  if (s <= 0) return 'Đã hết giờ';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0 ? `Còn ${h} giờ ${m} phút` : `Còn ${m}:${`${sec}`.padStart(2, '0')}`;
};

/**
 * Bình chọn bằng donate — phía NGƯỜI XEM.
 * Luật (sếp chốt): hết giờ phương án nhiều Ca nhất thắng, streamer làm theo;
 * Ca đã donate thuộc về creator dù thắng hay thua. Phải nói rõ TRƯỚC khi bấm.
 */
const CreatorDonatePoll: React.FC<{
  userId?: string;
  onAvailableChange?: (available: boolean) => void;
}> = ({ userId, onAvailableChange }) => {
  const [poll, setPoll] = useState<DonatePoll | null>(null);
  const [left, setLeft] = useState(0);
  const [amount, setAmount] = useState<string>('');
  const [votingId, setVotingId] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!userId) return;
    getPublicDonatePoll(userId)
      .then((p) => {
        setPoll(p);
        setLeft(p?.secondsLeft || 0);
      })
      .catch(() => setPoll(null));
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  // Đếm ngược tại chỗ, không phải hỏi lại máy chủ mỗi giây.
  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setInterval(() => setLeft((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [left > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const available = !!userId && !!poll?.exists && !!poll.isActive;

  // Báo lên cha (thanh tab Donate) biết tính năng này có đang hiện hay không, để ẩn/hiện tab
  // tương ứng. Dùng ref giữ callback mới nhất để effect chỉ chạy lại khi `available` đổi.
  const onAvailableChangeRef = useRef(onAvailableChange);
  onAvailableChangeRef.current = onAvailableChange;
  useEffect(() => {
    onAvailableChangeRef.current?.(available);
    // Unmount (vd đổi tab lớn rồi quay lại) -> báo về false, tránh cha giữ mãi trạng thái
    // available cũ (component đã biến mất nhưng cha vẫn tưởng tab này còn hiện được).
    return () => onAvailableChangeRef.current?.(false);
  }, [available]);

  if (!available || !poll) return null;

  const ended = poll.isEnded || left <= 0;

  const handleVote = (optionId: string) => {
    if (votingId) return; // chặn bấm 2 lần -> trừ Ca 2 lượt
    const value = Number(amount);
    if (!value || value <= 0) {
      toast.error('Vui lòng nhập số Ca muốn donate cho phương án.');
      return;
    }
    setVotingId(optionId);
    voteDonatePoll(userId, optionId, value)
      .then((p) => {
        setPoll(p);
        setLeft(p?.secondsLeft || 0);
        toast.success('Đã bình chọn!');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Bình chọn không thành công.');
      })
      .finally(() => setVotingId(null));
  };

  return (
    <div className="mt-8 box rounded-2xl p-4 md:p-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Vote className="text-blue-600" size={20} />
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Bình chọn</h3>
        </div>
        <span
          className={`shrink-0 text-sm font-semibold ${
            ended ? 'text-gray-400' : 'text-blue-600 dark:text-blue-400'
          }`}
        >
          {countdown(left)}
        </span>
      </div>

      <p className="mt-2 break-words text-base font-semibold text-gray-800 dark:text-white">
        {poll.question}
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Hết giờ, phương án nhiều Ca nhất thắng và creator sẽ làm theo.{' '}
        <span className="font-semibold">
          Ca đã donate thuộc về creator dù phương án bạn chọn thắng hay thua.
        </span>
      </p>

      <ul className="mt-4 flex flex-col gap-3">
        {poll.options.map((o) => (
          <li key={o.id}>
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="min-w-0 break-words font-medium text-gray-800 dark:text-white">
                {o.label}
                {ended && o.isLeading && ' 🏆'}
              </span>
              <span className="shrink-0 text-gray-500 dark:text-gray-400">
                {formatCa(o.totalAmount)} Ca · {o.percent}%
              </span>
            </div>
            <div className="mt-1 h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700">
              <div
                className={`h-full rounded-full transition-[width] duration-500 ${
                  o.isLeading ? 'bg-blue-600' : 'bg-blue-400/70'
                }`}
                style={{ width: `${o.percent}%` }}
              />
            </div>
            {!ended && (
              <button
                type="button"
                onClick={() => handleVote(o.id)}
                disabled={!!votingId}
                className="mt-2 w-full rounded-lg border border-blue-600 px-3 py-2 text-sm font-semibold text-blue-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-blue-400"
              >
                {votingId === o.id ? 'Đang gửi…' : 'Donate cho phương án này'}
              </button>
            )}
          </li>
        ))}
      </ul>

      {!ended && (
        <div className="mt-4 flex flex-col gap-1">
          <label htmlFor="poll-amount" className="text-sm">
            Số Ca cho mỗi lượt bình chọn
          </label>
          <input
            id="poll-amount"
            type="number"
            min={1}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="VD: 10000"
            className="h-11 w-full rounded-lg border border-[#C8CCD8] bg-white px-3 text-base dark:border-slate-600 dark:bg-slate-700 dark:text-white"
          />
        </div>
      )}

      <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
        Tổng đã bình chọn: {formatCa(poll.totalAmount)} Ca
      </p>
    </div>
  );
};

export default CreatorDonatePoll;
